import type { DocumentId, ExportWarning, JsonValue, SerializedNodeLike } from "./types";

/** A reference to something the host owns. The identity is stored; the label is only a fallback. */
export interface EntityReference {
  providerId: string;
  /** For example `person`, `note`, `project`, `meeting`. */
  entityType: string;
  entityId: string;
  /** What is shown inline and exported when the entity cannot be resolved. */
  fallbackLabel: string;
}

export interface EntitySuggestion {
  key: string;
  reference: EntityReference;
  description?: string;
}

export interface EntityProvider {
  id: string;
  triggers: readonly string[];
  search(input: {
    query: string;
    documentId: DocumentId;
    signal: AbortSignal;
  }): Promise<readonly EntitySuggestion[]>;
}

export interface EntityToken {
  kind: "entity";
  value: EntityReference;
}

export interface DateToken {
  kind: "date";
  /** A calendar date, `YYYY-MM-DD`. */
  isoDate: string;
  fallbackLabel: string;
}

export interface CustomToken {
  kind: "custom";
  tokenType: string;
  version: number;
  data: { [key: string]: JsonValue };
  fallbackLabel: string;
}

export type InlineToken = EntityToken | DateToken | CustomToken;

/** The serialized node type of every inline token. */
export const TOKEN_NODE_TYPE = "token";
/** The key the payload is stored under, at the top level of the serialized node. */
export const TOKEN_PAYLOAD_KEY = "token";

export interface MarkdownFallback {
  text: string;
  /** Whether the Markdown no longer identifies what the token pointed at. */
  lossy: boolean;
}

/** How one product-specific token type behaves. Everything has a label-based default. */
export interface TokenDefinition {
  tokenType: string;
  /** The version this build writes. Older stored tokens go through `migrate` when opened. */
  version: number;
  /** Returns what is wrong with `data`, or `null`. Runs before the document reaches the editor. */
  validate(data: { [key: string]: JsonValue }): string | null;
  /** Upgrades `data` written at an older `version`. Returning `null` leaves the token as stored. */
  migrate?(
    version: number,
    data: { [key: string]: JsonValue },
  ): { [key: string]: JsonValue } | null;
  markdown?(token: CustomToken): MarkdownFallback;
  /** Rendered as `data-token-appearance`, for styling. */
  appearance?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === "string" && value !== "";

function isJsonValue(value: unknown, depth = 0): value is JsonValue {
  if (depth > 32) return false;
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item, depth + 1));
  return isRecord(value) && Object.values(value).every((item) => isJsonValue(item, depth + 1));
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = ISO_DATE.exec(value);
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/** The label a token shows inline. It is also the node's text, so the two never disagree. */
export const tokenLabel = (token: InlineToken): string =>
  token.kind === "entity" ? token.value.fallbackLabel : token.fallbackLabel;

export interface TokenRegistry {
  readonly definitions: readonly TokenDefinition[];
  get(tokenType: string): TokenDefinition | undefined;
  /** Returns what is wrong with the payload, or `null` when it is a well-formed token. */
  check(payload: unknown): string | null;
  plainText(token: InlineToken): string;
  markdown(token: InlineToken): MarkdownFallback;
  appearance(token: InlineToken): string;
}

export function createTokenRegistry(definitions: readonly TokenDefinition[] = []): TokenRegistry {
  const byType = new Map(definitions.map((definition) => [definition.tokenType, definition]));

  const check = (payload: unknown): string | null => {
    if (!isRecord(payload)) return "token is not an object";
    if (payload.kind === "entity") {
      const value = payload.value;
      if (!isRecord(value)) return "entity token has no value";
      for (const field of ["providerId", "entityType", "entityId", "fallbackLabel"] as const) {
        if (!isText(value[field])) return `entity ${field} is missing`;
      }
      return null;
    }
    if (!isText(payload.fallbackLabel)) return "fallbackLabel is missing";
    if (payload.kind === "date") return isIsoDate(payload.isoDate) ? null : "isoDate is not a date";
    if (payload.kind !== "custom") return "token kind is not entity, date, or custom";
    if (!isText(payload.tokenType)) return "tokenType is missing";
    if (
      typeof payload.version !== "number" ||
      !Number.isInteger(payload.version) ||
      payload.version < 1
    ) {
      return "version is not a positive integer";
    }
    if (!isRecord(payload.data) || !isJsonValue(payload.data)) return "data is not a JSON object";
    // A type this build does not know, or another version of one it does (older ones are upgraded
    // by `migrateTokens` first), is kept as stored and shown by its label: the content is still
    // readable, and nothing here can judge its data.
    const definition = byType.get(payload.tokenType);
    if (definition === undefined || payload.version !== definition.version) return null;
    return definition.validate(payload.data as { [key: string]: JsonValue });
  };

  return {
    definitions,
    get: (tokenType) => byType.get(tokenType),
    check,
    // Always the label: it is the node's text, and Lexical's offsets depend on that.
    plainText: tokenLabel,
    markdown(token) {
      const custom = token.kind === "custom" ? byType.get(token.tokenType)?.markdown : undefined;
      if (custom !== undefined && token.kind === "custom") return custom(token);
      // A date says what it means in its label; a reference or an unknown token does not.
      return { text: tokenLabel(token), lossy: token.kind !== "date" };
    },
    appearance(token) {
      if (token.kind !== "custom") return token.kind === "entity" ? token.value.entityType : "date";
      return byType.get(token.tokenType)?.appearance ?? token.tokenType;
    },
  };
}

export const CORE_TOKEN_REGISTRY: TokenRegistry = createTokenRegistry();

/** The payload of a serialized token node, or `undefined` when the node is not a well-formed one. */
export function readToken(
  node: SerializedNodeLike,
  registry: TokenRegistry = CORE_TOKEN_REGISTRY,
): InlineToken | undefined {
  if (node.type !== TOKEN_NODE_TYPE) return undefined;
  const payload = node[TOKEN_PAYLOAD_KEY];
  return registry.check(payload) === null ? (payload as InlineToken) : undefined;
}

/**
 * Upgrades custom tokens stored at an older version, as a pure walk over the JSON. Returns its
 * input when nothing needed upgrading. A token whose migration declines stays as stored.
 */
export function migrateTokens(
  root: SerializedNodeLike,
  registry: TokenRegistry,
): { root: SerializedNodeLike; migrated: number } {
  const upgrade = (node: Record<string, unknown>): Record<string, unknown> | null => {
    if (node.type !== TOKEN_NODE_TYPE) return null;
    const payload = node[TOKEN_PAYLOAD_KEY];
    if (registry.check(payload) !== null) return null;
    const token = payload as InlineToken;
    if (token.kind !== "custom") return null;
    const definition = registry.get(token.tokenType);
    if (definition?.migrate === undefined || token.version >= definition.version) return null;
    const data = definition.migrate(token.version, token.data);
    if (data === null || definition.validate(data) !== null) return null;
    return { ...node, [TOKEN_PAYLOAD_KEY]: { ...token, version: definition.version, data } };
  };

  let pending = 0;
  const scan: unknown[] = [root];
  while (scan.length > 0) {
    const node = scan.pop();
    if (!isRecord(node)) continue;
    if (upgrade(node) !== null) pending++;
    if (Array.isArray(node.children)) for (const child of node.children) scan.push(child);
  }
  if (pending === 0) return { root, migrated: 0 };

  const copy: Record<string, unknown> = { ...root };
  const stack: Record<string, unknown>[] = [copy];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (!Array.isArray(node.children)) continue;
    const children: unknown[] = node.children.map((child: unknown) =>
      isRecord(child) ? (upgrade(child) ?? { ...child }) : child,
    );
    node.children = children;
    for (const child of children) if (isRecord(child)) stack.push(child);
  }
  return { root: copy as SerializedNodeLike, migrated: pending };
}

/** The warning an export reports when a token's identity does not survive it. */
export const identityLostWarning = (token: InlineToken, blockId?: string): ExportWarning => ({
  code: "token-identity-lost",
  message: `"${tokenLabel(token)}" was exported as text; what it referred to is not in the output.`,
  ...(blockId === undefined ? {} : { blockId }),
});
